import {
  Button,
  Stack,
  Box,
  Slide,
  Slider,
  styled,
  ClickAwayListener,
  CircularProgress,
} from "@mui/material";
import React, { useCallback, useEffect } from "react";
import { tease } from "../components/CheckButton";
import {
  AccountType,
  ButtonStyle,
  Color,
  ContactType,
  ErrorCode,
  FontStyle,
  JobDisplays,
  navPaddingTop,
  QuestionData,
  Slug,
  SurveyButtonStyle,
  SurveyColumn,
} from "../constants";
import FavoriteIcon from "@mui/icons-material/Favorite";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import { StyledTextField } from "../components/AutocompleteUtils";
import { useDispatch, useSelector } from "react-redux";
import { stringEquals } from "../utils/utils";
import SettingsSort from "../components/SettingsSort";
import EmailSlider from "../components/EmailSlider";
import * as apiService from "../service/ApiService";
import { useNavigate } from "react-router-dom";
import { logout } from "../service/AuthService";
import FettiRadio from "../components/FettiRadio";
import { jobIdFromUrl } from "../utils/utils";
import BasicApply from "../components/ApplyPanels/BasicApply";
import FettiProgressBar from "../components/FettiProgressBar";
import { updateApplyData } from "../utils/actionUtils";
import ResumeApply from "../components/ApplyPanels/ResumeApply";
import QuestionsApply from "../components/ApplyPanels/QuestionsApply";
import ReviewApply from "../components/ApplyPanels/ReviewApply";

const styles = {
  outer: {
    paddingTop: navPaddingTop,
  },
  container: {
    // marginTop: '67px',
    paddingTop: "3rem",
    paddingBottom: "110px",
    px: 4,
    backgroundColor: "#fff",
    // backgroundColor: "red",
    height: "100%",
    // alignItems: "stretch",
    minHeight: "calc(100vh - 67px)",
    width: "100%",
  },
  panel: {
    ...FontStyle.body,
    borderRadius: JobDisplays.borderRadius,
    backgroundColor: JobDisplays.backgroundColor,
    // boxShadow: JobDisplays.boxShadow,
    boxShadow: "1px 1px 8px rgb(0 0 0 / 18%)",
    padding: JobDisplays.padding,
    width: "100%",
    maxWidth: "500px",
  },
};

// Basic Info
// First/Last name
// Email
// Location
// Phone Number
const lastPageIdx = 3;
const ApplyPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryParameters = new URLSearchParams(window.location.search);
  const paramJobId = queryParameters.get("jobId");
  const jobId = jobIdFromUrl(paramJobId);
  const auth = useSelector((state) => state.auth);
  const apply = useSelector((state) => state.apply);
  const serverQuestions = useSelector(
    (state) => state.apply.serverData.questions
  );
  const [submitLoading, setSubmitLoading] = React.useState(false);
  const loggedIn = auth.testing
    ? true
    : auth.accountData.loginToken !== null &&
      auth.accountData.contactInfo !== null;
  const serverLoaded = apply.jobId === jobId;
  useEffect(() => {
    console.log("apply page", loggedIn, paramJobId, serverLoaded);
    if (!loggedIn) return;
    if (!paramJobId) return;
    if (serverLoaded) return;
    console.log("loading job data", jobId);
    apiService.getApplyData(dispatch, navigate, { ...auth.accountData, jobId });
  }, [paramJobId, apply.jobId, loggedIn]);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [apply.step]);

  if (!serverLoaded) {
    return (
      <Stack
        direction="column"
        alignItems="center"
        sx={{
          ...styles.outer,
        }}>
        <Stack sx={styles.container} direction="column" alignItems="center">
          <Stack sx={styles.panel}>
            <CircularProgress />
          </Stack>
        </Stack>
      </Stack>
    );
  }
  const canContinue = () => {
    if (apply.step === 0) {
      return (
        apply.app.firstName &&
        apply.app.lastName &&
        apply.app.email &&
        apply.app.email.includes("@")
      );
    }
    if (apply.step === 1) {
      return apply.app.resume;
    }
    if (apply.step === 2) {
      return apply.app.questions.every((q) => q !== "");
    }
    if (apply.step === lastPageIdx) {
      return !submitLoading;
    }
    return false;
  };
  const canBack = () => {
    return apply.step > 0;
  };
  const getApplicationData = () => {
    const application = {
      first_name: apply.app.firstName,
      last_name: apply.app.lastName,
      email: apply.app.email,
      location: apply.app.location,
      phone: apply.app.phone,
      resume_id: apply.app.resume[0],
      responses: apply.app.questions,
    };
    return application;
  };
  const handleContinue = () => {
    if (apply.step === 0) {
      const missing = [];
      ["firstName", "lastName", "email"].forEach((key) => {
        if (!apply.app[key]) {
          missing.push(key);
        }
      });
      updateApplyData(dispatch, { missing });
      if (missing.length === 0) {
        updateApplyData(dispatch, { step: 1 });
      }
    }
    if (apply.step === 1) {
      if (serverQuestions && serverQuestions.length > 0) {
        updateApplyData(dispatch, { step: 2 });
      } else {
        updateApplyData(dispatch, { step: 3 });
      }
    }
    if (apply.step === 2) {
      updateApplyData(dispatch, { step: 3 });
    }
    if (apply.step === lastPageIdx) {
      const application = getApplicationData();
      console.log("application", application);
      setSubmitLoading(true);
      apiService.submitApplication(
        dispatch,
        {
          ...auth.accountData,
          jobId,
          application,
        },
        {
          handleFinish: () => {
            setSubmitLoading(false);
          },
          handleSuccess: () => {
            navigate(Slug.applySuccess);
          },
        }
      );
    }
  };
  const handleBack = () => {
    if (
      apply.step === 3 &&
      (!serverQuestions || serverQuestions.length === 0)
    ) {
      updateApplyData(dispatch, { step: 1 });
    } else {
      updateApplyData(dispatch, { step: apply.step - 1 });
    }
  };

  return (
    <Stack
      direction="column"
      alignItems="center"
      sx={{
        ...styles.outer,
      }}>
      <Stack
        sx={styles.container}
        direction="column"
        alignItems="center"
        spacing={3}>
        <Stack sx={styles.panel} spacing={2}>
          <Box
            sx={{
              width: "100%",
              textAlign: "center",
              ...FontStyle.logo,
              fontSize: { xs: "24px", md: "40px" },
            }}>
            Fetti Quick Apply
          </Box>
          <Box
            sx={{
              width: "100%",
              textAlign: "left",
              ...FontStyle.body,
              fontSize: "16px",
              color: Color.secondaryTextGray,
            }}>
            Answer a few basic questions and we'll submit your application to{" "}
            {apply.jobData.company}.
          </Box>
          <FettiProgressBar
            pct={25 + ((100 - 25) * apply.step) / lastPageIdx}
          />
        </Stack>
        <Stack sx={styles.panel} spacing={2}>
          {apply.step === 0 && <BasicApply />}
          {apply.step === 1 && <ResumeApply />}
          {apply.step === 2 && <QuestionsApply />}
          {apply.step === 3 && <ReviewApply />}
          {/* todo: fix button styling at the end */}
          <Button
            sx={{
              ...SurveyButtonStyle.defaultStyling,
              ...SurveyButtonStyle.primaryNoShadow,
              ...(!canContinue() && SurveyButtonStyle.disabledPrimary),
            }}
            disableRipple
            disabled={!canContinue()}
            fullWidth
            onClick={handleContinue}>
            {apply.step === lastPageIdx ? (
              submitLoading ? (
                <CircularProgress size="20px" />
              ) : (
                "Submit"
              )
            ) : (
              "Continue"
            )}
          </Button>
          {canBack() && (
            <Button
              sx={{
                ...SurveyButtonStyle.defaultStyling,
                ...SurveyButtonStyle.grayColor,
              }}
              disableRipple
              fullWidth
              onClick={handleBack}>
              Back
            </Button>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default ApplyPage;
